// GarageScene.ts
import * as THREE from 'three';
import AssetManager from '../managers/AssetManager';
import { applySkinToObject, getTextureForSkin } from '../utils/skin';

export default class GarageScene {
  camera: THREE.Camera;
  scene: THREE.Scene;
  collidables: any[];
  tankModel: THREE.Object3D | null;
  turret: THREE.Object3D | null;
  stand: THREE.Object3D | null;
  textures: Record<string, any>;
  mouseVelocityX: number;
  ui: any;
  cannon?: THREE.Object3D;

  constructor(camera: THREE.Camera, ui: any, renderer: any) {
    this.camera = camera;
    this.scene = new THREE.Scene();
    this.collidables = [];
    this.tankModel = null;
    this.turret = null;
    this.stand = null;
    this.textures = {} as Record<string, any>;
    this.mouseVelocityX = 0;
    this.ui = ui;
  }

  initScene() {
    this.loadTextures();
    this.loadTankModel();
    this.initEnvironment();
    this.initLights();
    this.initCameraPosition();
  }

  initCameraPosition() {
    (this.camera as any).position.set(0, 3, 6);
    if (this.tankModel) (this.camera as any).lookAt(this.tankModel.position.clone());
  }

  activate() {
    const changeTankTexture = (skinName: string) => {
      if (!this.tankModel) return;
      const tex = getTextureForSkin(skinName);
      if (tex) applySkinToObject(this.tankModel, tex);
    };
    this.ui.init(changeTankTexture);
    // (this.scene as any).background = AssetManager.getCubeTexture('alaSkyboxTexture');
    // this.scene.background = AssetManager.getCubeTexture('alaSkyboxTexture');
  }

  deactivate() {
    this.ui.dispose();
  }

  loadTextures() {
    this.textures['T90A'] = AssetManager.getTexture('textureA');
    this.textures['T90B'] = AssetManager.getTexture('textureB');
    this.textures['T90C'] = AssetManager.getTexture('textureC');
    this.textures['T90D'] = AssetManager.getTexture('textureD');
    this.textures['T90E'] = AssetManager.getTexture('textureE');
    this.textures['T90F'] = AssetManager.getTexture('textureF');
    this.textures['T90G'] = AssetManager.getTexture('textureG');
  }

  loadTankModel() {
    const model = AssetManager.getModel('tankModel') as THREE.Object3D;
    this.tankModel = model;
    this.tankModel.traverse((child: THREE.Object3D) => {
      if ((child as any).isMesh) {
        const mesh = child as any;
        if (mesh.material) mesh.material = mesh.material.clone();
        mesh.castShadow = true;
      }
      if (child.name === 'Main_Gun') this.cannon = child;
      if (child.name === 'TopPart') this.turret = child;
    });
    const initialTex = getTextureForSkin('T90A');
    if (initialTex && this.tankModel) {
      applySkinToObject(this.tankModel, initialTex);
    }
    this.tankModel.scale.set(0.01, 0.01, 0.01);
    this.tankModel.position.set(0, 0.6, 0);
    this.scene.add(this.tankModel);
  }

  initEnvironment() {
    const floorTexture = AssetManager.getTexture('grassyStoneTexture') as any;
    floorTexture.repeat.set(15, 15);
    floorTexture.wrapS = THREE.RepeatWrapping;
    floorTexture.wrapT = THREE.RepeatWrapping;

    const floorMaterial = new THREE.MeshStandardMaterial({
      map: floorTexture,
      side: THREE.DoubleSide,
      roughness: 0.8,
      metalness: 0.2,
    });

    const floor = new THREE.Mesh(new THREE.PlaneGeometry(50, 50), floorMaterial);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -0.5;
    floor.receiveShadow = true;
    this.scene.add(floor);

    const standTexture = AssetManager.getTexture('rustyMetalTexture') as any;
    const standMaterial = new THREE.MeshStandardMaterial({
      map: standTexture,
      side: THREE.DoubleSide,
      bumpMap: standTexture,
      bumpScale: 0.05,
    });
    const standGeometry = new THREE.CylinderGeometry(5, 5, 1, 256);
    const stand = new THREE.Mesh(standGeometry, standMaterial);
    stand.position.y = -0.95;
    stand.receiveShadow = true;
    this.stand = stand;
    this.scene.add(stand);
  }

  initLights() {
    const ambientLight = new THREE.AmbientLight(0x404040, 1);
    this.scene.add(ambientLight);
    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(5, 10, 7.5);
    directionalLight.castShadow = true;
    this.scene.add(directionalLight);
  }

  update(deltaTime: number) {
    this.updateTurretRotation(deltaTime);
  }

  updateTurretRotation(deltaTime: number) {
    if (!this.tankModel || !this.stand) return;
    const rotation = (deltaTime * Math.PI) / 8;
    this.tankModel.rotation.y += rotation;
    this.stand.rotation.y += rotation;
  }

  disposeScene() {
    this.scene.traverse((object: any) => {
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        if (Array.isArray(object.material)) {
          object.material.forEach((material: any) => material.dispose());
        } else {
          object.material.dispose();
        }
      }
    });
  }

  getThreeScene(): THREE.Scene {
    return this.scene;
  }
}
